import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// * DRAGULA IMPORTS =============================================================================
import { DragulaModule } from 'ng2-dragula';

// * PRIMENG IMPORTS =============================================================================
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { CalendarModule } from 'primeng/calendar';
import { SliderModule } from 'primeng/slider';
import { MultiSelectModule } from 'primeng/multiselect';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessagesModule } from 'primeng/messages';
import { MessageModule } from 'primeng/message';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { PanelModule } from 'primeng/panel';
import { PaginatorModule } from 'primeng/paginator';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TabViewModule } from 'primeng/tabview';
import { PanelMenuModule } from 'primeng/panelmenu';
import { FileUploadModule } from 'primeng/fileupload';

// * Angular imports =============================================================================
import { LoginComponent } from './login/login.component';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { NavbarComponent } from './shared/navbar/navbar.component';
import { TopHeaderComponent } from './shared/top-header/top-header.component';
import { OperationsComponent } from './operations/operations.component';
import { OperationsAddComponent } from './operations/operations-add/operations-add.component';
import { UsersComponent } from './users/users.component';
import { JwtInterceptor } from './_helpers/jwt.interceptor';
import { ErrorInterceptor } from './_helpers/error.interceptor';
import { ProductsComponent } from './products/products.component';
import { UsersAddComponent } from './users/users-add/users-add.component';
import { ProductTypesComponent } from './product-types/product-types.component';
import { RolesPipe } from './_pipes/roles.pipe';
import { ProductsAddComponent } from './products/products-add/products-add.component';
import { ProductTypesEditComponent } from './product-types/product-types-edit/product-types-edit.component';
import { ProductTypesAddComponent } from './product-types/product-types-add/product-types-add.component';
import { OperationsEditComponent } from './operations/operations-edit/operations-edit.component';
import { ProductsEditComponent } from './products/products-edit/products-edit.component';
import { ProductsCopyComponent } from './products/products-copy/products-copy.component';
import { PackageTypesComponent } from './package-types/package-types.component';
import { PackageTypesAddComponent } from './package-types/package-types-add/package-types-add.component';
import { PackageTypesEditComponent } from './package-types/package-types-edit/package-types-edit.component';
import { PartnersComponent } from './partners/partners.component';
import { PartnersAddComponent } from './partners/partners-add/partners-add.component';
import { PartnersEditComponent } from './partners/partners-edit/partners-edit.component';
import { StatusComponent } from './status/status.component';
import { StatusAddComponent } from './status/status-add/status-add.component';
import { StatusEditComponent } from './status/status-edit/status-edit.component';
import { NipComponent } from './nip/nip.component';
import { NipAddComponent } from './nip/nip-add/nip-add.component';
import { NipEditComponent } from './nip/nip-edit/nip-edit.component';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { EmployeeResponsibilitiesComponent } from './employee-responsibilities/employee-responsibilities.component';
import { NipWorkerComponent } from './nip-worker/nip-worker.component';
import { NipWorkerEditComponent } from './nip-worker/nip-worker-edit/nip-worker-edit.component';
import { UsersEditComponent } from './users/users-edit/users-edit.component';
import { ReasonsPipe } from './_pipes/reasons.pipe';
import { PriorityPipe } from './_pipes/priority.pipe';
import { MachinsAllComponent } from './machines/machins-all/machins-all.component';
import { MachinsEditComponent } from './machines/machins-edit/machins-edit.component';
import { MachinsAddComponent } from './machines/machins-add/machins-add.component';
import { FileUploadComponent } from './shared/file-upload/file-upload.component';
import { InfiniteDropdownComponent } from './shared/infinite-dropdown/infinite-dropdown.component';

import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { DashboardComponent } from './dashboard/dashboard.component';
import { DailyReportComponent } from './daily-report/daily-report.component';
import { PeriodicReportComponent } from './periodic-report/periodic-report.component';
import { NipAllImprovedComponent } from './nip/nip-all-improved/nip-all-improved.component';
import { TableComponent } from './shared/table/table.component';
import { NipEditImprovedComponent } from './nip/nip-edit-improved/nip-edit-improved.component';
import { PartnerAllImprovedComponent } from './partners/partner-all-improved/partner-all-improved.component';
import { PartnerEditImprovedComponent } from './partners/partner-edit-improved/partner-edit-improved.component';
import { NipWorkerAllImprovedComponent } from './nip-worker/nip-worker-all-improved/nip-worker-all-improved.component';
import { NipWorkerEditImprovedComponent } from './nip-worker/nip-worker-edit-improved/nip-worker-edit-improved.component';
import { BlockUiComponent } from './shared/block-ui/block-ui.component';
import { ProductsAllImprovedComponent } from './products/products-all-improved/products-all-improved.component';
import { ProductReportComponent } from './reports/product-report/product-report.component';
import { ScrapReportComponent } from './reports/scrap-report/scrap-report.component';
import { DelayReportComponent } from './reports/delay-report/delay-report.component';
import { OnlineNipComponent } from './online-nip/online-nip/online-nip.component';
import { WorkerReportComponent } from './reports/worker-report/worker-report.component';
import { WorkerDelayComponent } from './reports/worker-delay/worker-delay.component';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    AuthLayoutComponent,
    MainLayoutComponent,
    NavbarComponent,
    TopHeaderComponent,
    OperationsComponent,
    OperationsAddComponent,
    UsersComponent,
    ProductsComponent,
    UsersAddComponent,
    ProductTypesComponent,
    RolesPipe,
    ProductsAddComponent,
    ProductTypesEditComponent,
    ProductTypesAddComponent,
    OperationsEditComponent,
    ProductsEditComponent,
    ProductsCopyComponent,
    PackageTypesComponent,
    PackageTypesAddComponent,
    PackageTypesEditComponent,
    PartnersComponent,
    PartnersAddComponent,
    PartnersEditComponent,
    StatusComponent,
    StatusAddComponent,
    StatusEditComponent,
    NipComponent,
    NipAddComponent,
    NipEditComponent,
    EmployeeResponsibilitiesComponent,
    NipWorkerComponent,
    NipWorkerEditComponent,
    UsersEditComponent,
    ReasonsPipe,
    PriorityPipe,
    MachinsAllComponent,
    MachinsEditComponent,
    MachinsAddComponent,
    FileUploadComponent,
    DashboardComponent,
    DailyReportComponent,
    PeriodicReportComponent,
    NipAllImprovedComponent,
    TableComponent,
    NipEditImprovedComponent,
    PartnerAllImprovedComponent,
    PartnerEditImprovedComponent,
    NipWorkerAllImprovedComponent,
    NipWorkerEditImprovedComponent,
    BlockUiComponent,
    ProductsAllImprovedComponent,
    ProductReportComponent,
    ScrapReportComponent,
    DelayReportComponent,
    OnlineNipComponent,
    WorkerReportComponent,
    WorkerDelayComponent,
    InfiniteDropdownComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    // * DRAGULA IMPORTS
    DragulaModule.forRoot(),

    // * PRIMENG IMPORTS
    ButtonModule,
    InputTextModule,
    InputSwitchModule,
    ReactiveFormsModule,
    TableModule,
    TooltipModule,
    ToastModule,
    CalendarModule,
    SliderModule,
    MultiSelectModule,
    ContextMenuModule,
    DialogModule,
    DropdownModule,
    ProgressBarModule,
    ProgressSpinnerModule,
    MessagesModule,
    ConfirmPopupModule,
    MessageModule,
    InputTextareaModule,
    PanelModule,
    PaginatorModule,
    CheckboxModule,
    RadioButtonModule,
    SelectButtonModule,
    TabViewModule,
    FileUploadModule,
    PanelMenuModule,
    NgIdleKeepaliveModule.forRoot(), // use NgIdleModule.forRoot() if not using keepalive
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    MessageService,
    ConfirmationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
